import React, { useEffect } from 'react';
import { Chart, registerables } from 'chart.js';
import { Order } from 'core/domains/order';
import OverviewBox from 'components/overview-box/overview-box';
import './orders-piechart-payment.scss';
import combineChartOrdersPayments from 'components/order-payment/combine-chart-orders-payments';

// Register all components
Chart.register(...registerables);


// Define the props for the component
interface OrderPieChartProps {
    orders: Order[]; // Array of orders
}

const OrderPieChartByPayments: React.FC<OrderPieChartProps> = ({ orders }) => {
    const chartRef = React.useRef<Chart | null>(null); // Reference for the chart instance

    useEffect(() => {
        // Count the occurrences of each order type
        const orderCounts = orders.reduce<Record<string, number>>((acc, order: Order) => {
            acc[order.paymentType.title] = (acc[order.paymentType.title] || 0) + 1;
            return acc;
        }, {});

        // Prepare the data for the pie chart
        const data = {
            labels: Object.keys(orderCounts),
            datasets: [{
                data: Object.values(orderCounts),
                backgroundColor: ['rgba(153, 102, 255, 0.6)', 'rgba(255, 159, 64, 0.6)', 'rgba(255, 206, 86, 0.6)', 'rgba(255, 99, 132, 0.6)', 'rgba(54, 162, 235, 0.6)'],
                hoverBackgroundColor: ['rgba(153, 102, 255, 0.8)', 'rgba(255, 159, 64, 0.8)', 'rgba(255, 206, 86, 0.8)', 'rgba(255, 99, 132, 0.8)', 'rgba(54, 162, 235, 0.8)'],
            }]
        };
        const totalAmount = Object.values(orderCounts).reduce((sum, amount) => sum + amount, 0);

        // Configuration for the pie chart
        const config = {
            type: 'doughnut',
            data: data,
            options: {
                responsive: true,
                plugins: {
                    legend: {
                        display: true,
                        position: 'top',
                        labels: {
                            font: {
                                family: 'YekanBakh', // Set font family for legend
                                size: 14, // Set font size for legend
                            },
                        },
                    },
                    tooltip: {
                        titleFont: {
                            family: 'YekanBakh', // Set font family for legend
                            size: 14, // Set font size for tooltip title
                        },
                        bodyFont: {
                            family: 'YekanBakh', // Set font family for legend
                            size: 12, // Set font size for tooltip body
                        },
                        callbacks: {
                            label: function (tooltipItem: any) {
                                const label = tooltipItem.label || '';
                                const value = tooltipItem.raw || 0;
                                return `${label}: ${value} سفارش`;
                            }
                        }
                    }
                }
            }
        };
        // Get the context of the canvas
        const ctx = document.getElementById('orderPieChartByPayments') as HTMLCanvasElement;

        // Destroy the previous chart instance if it exists
        if (chartRef.current) {
            chartRef.current.destroy();
        }

        // Create a new chart and store the reference
        chartRef.current = new Chart(ctx.getContext('2d') as CanvasRenderingContext2D, config as any);

        // Cleanup function to destroy the chart on component unmount
        return () => {
            if (chartRef.current) {
                chartRef.current.destroy();
            }
        };
    }, [orders]);

    const getTotalValueOfCartOrders = orders.length > 0 ? orders.filter(s => s.state.id == 2 && s.paymentType.id == 1).reduce((accumulator, order) => {
        return accumulator + order.amount;
    }, 0) : 0;

    const getTotalValueOfGatewayOrders = orders.length > 0 ? orders.filter(s => s.state.id == 2 && s.paymentType.id == 2).reduce((accumulator, order) => {
        return accumulator + order.amount;
    }, 0) : 0;


    const getTotalValueOfWalletOrders = orders.length > 0 ? orders.filter(s => s.state.id == 2 && s.paymentType.id == 3).reduce((accumulator, order) => {
        return accumulator + order.amount;
    }, 0) : 0;


    const getTotalValueOfPerfectMoneyOrders = orders.length > 0 ? orders.filter(s => s.state.id == 2 && s.paymentType.id == 4).reduce((accumulator, order) => {
        return accumulator + order.amount;
    }, 0) : 0;

    const getTotalValueOfTronOrders = orders.length > 0 ? orders.filter(s => s.state.id == 2 && s.paymentType.id == 5).reduce((accumulator, order) => {
        return accumulator + order.amount;
    }, 0) : 0;


    return (
        <div className='overview-order-piechart-payments'>
            <canvas id="orderPieChartByPayments" width="200" height="200"></canvas>
            <div className="order-payments-info">
                <div className="row">
                    <OverviewBox
                        hideCount
                        title={'کارت به کارت'}
                        value={getTotalValueOfCartOrders}
                        count={orders.length > 0 ? orders.filter(s => s.state.id == 2 && s.paymentType.id == 1).length : 0}
                    />
                    <OverviewBox
                        hideCount
                        title={'درگاه بانکی'}
                        value={getTotalValueOfGatewayOrders}
                        count={orders.length > 0 ? orders.filter(s => s.state.id == 2 && s.paymentType.id == 2).length : 0}
                    />
                </div>
                <div className="row">
                    <OverviewBox
                        hideCount
                        title={'کیف پول'}
                        value={getTotalValueOfWalletOrders}
                        count={orders.length > 0 ? orders.filter(s => s.state.id == 2 && s.paymentType.id == 3).length : 0}
                    />
                    <OverviewBox
                        hideCount
                        title={'پرفکت مانی'}
                        value={getTotalValueOfPerfectMoneyOrders}
                        count={orders.length > 0 ? orders.filter(s => s.state.id == 2 && s.paymentType.id == 4).length : 0}
                    />
                </div>
                <div className="row">
                    <OverviewBox
                        hideCount
                        title={'ترون'}
                        value={getTotalValueOfTronOrders}
                        count={orders.length > 0 ? orders.filter(s => s.state.id == 2 && s.paymentType.id == 5).length : 0}
                    />
                </div>

            </div>
        </div>
    );
};


export default OrderPieChartByPayments;
